import { Button } from "primereact/button";
import {
  DataTable,
  DataTableSelectionMultipleChangeEvent,
  DataTableValueArray,
} from "primereact/datatable";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { EventUsers } from "../../models/events";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import {
  eventsApi,
  useDeleteEventUsersMutation,
  useGetEventUsersQuery,
  useSavePaymentsMutation,
  useUnsavePaymentsMutation,
} from "../../store/services/events";
import {
  useBanUsersMutation,
  useUnbanUsersMutation,
} from "../../store/services/users";
import toast from "react-hot-toast";
import { Dialog } from "primereact/dialog";
// import { SelectUsers } from "../SelectUsers"; // Temporarily disabled
import { sortUsersByPaidStatus } from "../../utils/functions/users";
import { isUserRestrictedByBan } from "../../utils/functions/users";
import { PhoneBodyTemplate } from "../TableComponents/PhoneNumberTemplate";
import { PaymentBodyTemplate } from "../TableComponents/PaymentTemplate";
import { SelectUsers } from "../SelectUsers";
import { isObjectRecord } from "../../utils/functions/api-errors";
import { AppDispatch } from "../../store";
import { RemoveEventUserOutcome } from "../../models/forms";

interface InscriptionsDTProps {
  eventId: number;
  mode: "user" | "fictional";
  validateDate?: boolean;
  /**
   * Evento pago por GRUPS: la baja puede devolver plata, así que el admin tiene
   * que decidir explícitamente antes de eliminar (GTK-736).
   */
  isPaidEvent?: boolean;
}

type ExpandedRows = DataTableValueArray | Record<string, boolean>;

const isSelectedEventUser = (value: unknown): value is EventUsers =>
  isObjectRecord(value) && typeof value.userId === "number";

/** Mensajes por código de error del worker; el fallback es lo que mandó el backend. */
const removalFailureReason = (outcome: RemoveEventUserOutcome): string => {
  switch (outcome.error?.code) {
    case "REFUND_FAILED":
      return "MercadoPago rechazó el reembolso. La inscripción sigue activa.";
    case "NOT_FOUND":
      return "La inscripción ya no estaba activa.";
    default:
      return outcome.error?.message ?? "Error desconocido.";
  }
};

export const InscriptionsDT = ({
  eventId,
  mode,
  validateDate,
  isPaidEvent,
}: InscriptionsDTProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [realUsersToAdd, setRealUsersToAdd] = useState<number>();
  const [fictionalUsersToAdd, setFictionalUsersToAdd] = useState<number>();
  const [usersSelected, setUsersSelected] = useState<EventUsers[]>([]);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [failedRemovals, setFailedRemovals] = useState<RemoveEventUserOutcome[]>([]);
  // Se recuerda para que el reintento no cambie por su cuenta lo que el admin
  // ya decidió sobre la plata.
  const [lastRefundChoice, setLastRefundChoice] = useState(false);
  const userType = mode === "user" ? 2 : 3;

  const {
    data: eventUsers,
    isLoading: isLoadingEventUsers,
  } = useGetEventUsersQuery(eventId);

  const [banUsers, { isLoading: isLoadingBan }] =
    useBanUsersMutation();

  const [unbanUsers] =
    useUnbanUsersMutation();

  const [deleteEventUsers, { isLoading: isLoadingDelete }] =
    useDeleteEventUsersMutation();

  const [saveUsersPayment, { isLoading: isLoadingPayment }] =
    useSavePaymentsMutation();

  const [unsaveUsersPayment, { isLoading: isLoadingUnsavePaymenty }] =
    useUnsavePaymentsMutation();

  const restrictedSelectedUsers = usersSelected.filter((user) => isUserRestrictedByBan(user));
  const paymentEligibleUsers = usersSelected.filter((user) => !isUserRestrictedByBan(user));
  const selectedUserIds = usersSelected.map((user) => user.userId);
  // Para poder nombrar en el detalle de fallas a quien siguió inscripto.
  const usersById = new Map(
    (Array.isArray(eventUsers) ? eventUsers : []).map((user) => [user.userId, user])
  );

  const hideAddRealUsersDialog = () => {
    setRealUsersToAdd(undefined);
  };
  const hideAddFictionalUsersDialog = () => {
    setFictionalUsersToAdd(undefined);
  };

  const invalidateEventUsers = () => {
    dispatch(
      eventsApi.util.invalidateTags([{ type: "events", id: `${eventId}-users` }])
    );
  };

  const handleBan = () => {
    const usersToBan = usersSelected.filter(user => !isUserRestrictedByBan(user));
    const usersToUnban = usersSelected.filter(user => isUserRestrictedByBan(user));

    const promises: Array<Promise<void>> = [];

    if (usersToBan.length > 0) {
      promises.push(
        banUsers({
          userIds: usersToBan.map((user) => user.userId),
        }).unwrap()
      );
    }

    if (usersToUnban.length > 0) {
      promises.push(
        unbanUsers({
          userIds: usersToUnban.map((user) => user.userId),
        }).unwrap()
      );
    }

    Promise.all(promises)
      .then(() => {
        const action = usersToBan.length > 0 && usersToUnban.length > 0
          ? "actualizados"
          : usersToBan.length > 0
            ? "baneados"
            : "desbaneados";
        toast.success(`Usuarios ${action} globalmente!`);
      })
      .catch(() => toast.error("No se pudo actualizar el estado global de los usuarios"))
      .finally(() => {
        invalidateEventUsers();
        setUsersSelected([]);
      });
  };

  // Los usuarios ficticios no tienen pago asociado, así que el reembolso sólo
  // se pregunta en la tabla de inscriptos reales de un evento pago por GRUPS.
  const refundApplies = !!isPaidEvent && mode === "user";

  const runDelete = (usersIds: number[], refund: boolean) => {
    setShowDeleteDialog(false);
    setLastRefundChoice(refund);

    deleteEventUsers({ id: eventId, usersIds, refund })
      .unwrap()
      .then((result) => {
        const failed = result.results.filter((outcome) => !outcome.removed);
        setFailedRemovals(failed);

        if (failed.length === 0) {
          toast.success(
            result.results.some((outcome) => outcome.refundProcessed)
              ? "Usuarios eliminados y reembolsos procesados!"
              : "Usuarios eliminados del evento exitosamente!"
          );
          return;
        }

        // La baja de cada usuario es atómica: los que fallaron conservan su
        // inscripción, así que el admin tiene que poder reintentar sólo esos.
        toast.error(
          result.removedCount === 0
            ? "No se pudo dar de baja ninguna inscripción"
            : `Se dieron de baja ${result.removedCount} de ${result.results.length} inscripciones`
        );
      })
      .catch((error) => {
        // Fallas del request entero (evento inexistente, red, 5xx del worker).
        // Un reembolso rechazado no llega acá: vuelve dentro del 207.
        const typedError = error as {
          data?: { code?: string; error?: { code?: string } };
        };
        const errorCode = typedError?.data?.error?.code ?? typedError?.data?.code;

        toast.error(
          errorCode === "REFUND_FAILED"
            ? "No se pudo procesar el reembolso. La inscripción sigue activa, reintentá en unos minutos."
            : "No se pudieron eliminar los usuarios al evento"
        );
      })
      .finally(() => {
        setUsersSelected([]);
      });
  };

  const handleDelete = () => {
    setFailedRemovals([]);
    setShowDeleteDialog(true);
  };

  const handleRetryFailed = () => {
    const usersIds = failedRemovals.map((outcome) => outcome.userId);
    setFailedRemovals([]);
    runDelete(usersIds, lastRefundChoice);
  };

  const handlePayment = () => {
    if (!paymentEligibleUsers.length) {
      toast.error("No se puede iniciar pago para usuarios con restricción de acceso");
      return;
    }

    if (restrictedSelectedUsers.length > 0) {
      toast.error(`Se omitieron ${restrictedSelectedUsers.length} usuario(s) con restricción de acceso`);
    }

    saveUsersPayment({
      id: eventId,
      usersIds: [...paymentEligibleUsers.map((user) => user.userId)],
    })
      .unwrap()
      .then(() => {
        toast.success("Los pagos se han guardado correctamente!");
      })
      .catch(() => toast.error("No se pudieron guardar los pagos"))
      .finally(() => {
        setUsersSelected([]);
      });
  };

  const handleUnsavePayment = () => {
    unsaveUsersPayment({
      id: eventId,
      usersIds: [...usersSelected.map((user) => user.userId)],
    })
      .unwrap()
      .then(() => {
        toast.success("Los pagos se han guardado correctamente!");
      })
      .catch(() => toast.error("No se pudieron guardar los pagos"))
      .finally(() => {
        setUsersSelected([]);
      });
  };

  const rowClassName = (user: EventUsers) => {
    return {
      "bg-green-100": user.paid,
    };
  };

  const allowExpansion = (rowData: EventUsers) => {
    return !!(rowData.companions && rowData.companions.length > 0);
  };

  const rowExpansionTemplate = (data: EventUsers) => {
    return (
      <div className="p-3 pl-6">
        <h4 className="mt-0 mb-2" style={{ fontSize: "0.95rem", color: "#666" }}>
          Invitados
        </h4>
        <DataTable value={data.companions || []} size="small">
          <Column field="firstName" header="Nombre" />
          <Column field="lastName" header="Apellido" />
          <Column field="email" header="Email" />
        </DataTable>
      </div>
    );
  };

  return (
    <>
      <DataTable
        value={sortUsersByPaidStatus(
          (eventUsers && Array.isArray(eventUsers) ? eventUsers.filter((item) => item.userTypeId === userType) : [])
        )}
        loading={
          isLoadingBan ||
          isLoadingDelete ||
          isLoadingEventUsers ||
          isLoadingPayment ||
          isLoadingUnsavePaymenty
        }
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        header={() => (
          <div className="flex flex-column md:flex-row justify-content-between align-items-center">
            <div className="flex align-items-center gap-2">
              <h1 className="text-2xl font-bold m-0 p-0">
                Lista de usuarios {mode === "user" ? "reales" : "ficticios"}
              </h1>
              {!validateDate && (
                <Button
                  icon="pi pi-plus"
                  rounded
                  size="small"
                  onClick={() =>
                    mode === "user"
                      ? setRealUsersToAdd(eventId)
                      : setFictionalUsersToAdd(eventId)
                  }
                />
              )}
            </div>
            <div className="flex gap-2 flex-wrap mt-4 md:flex-nowrap md:mt-0">
              {mode === "user" && (
                <Button
                  disabled={!usersSelected.length}
                  outlined
                  icon="pi pi-ban"
                  severity="warning"
                  size="small"
                  label="Banear/Desbanear"
                  onClick={handleBan}
                />
              )}
              <Button
                disabled={!usersSelected.length}
                outlined
                icon="pi pi-user-minus"
                severity="danger"
                size="small"
                label="Eliminar"
                onClick={handleDelete}
              />
              {mode === "user" && (
                <Button
                  disabled={!paymentEligibleUsers.length}
                  outlined
                  icon="pi pi-money-bill"
                  severity="success"
                  size="small"
                  label="Pagar"
                  onClick={handlePayment}
                />
              )}
              {mode === "user" && (
                <Button
                  disabled={!usersSelected.length}
                  outlined
                  icon="pi pi-money-bill"
                  severity="danger"
                  size="small"
                  label="Deshacer pago"
                  onClick={handleUnsavePayment}
                />
              )}
            </div>
          </div>
        )}
        paginator
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        rowClassName={rowClassName}
        dataKey="eventUserId"
        className="my-4"
        filterDisplay="row"
        globalFilterFields={[
          "userEmail",
          "userLastName",
          "userFirstName",
          "phone",
        ]}
        selection={usersSelected}
        selectionMode="multiple"
        onSelectionChange={({
          value,
        }: DataTableSelectionMultipleChangeEvent<DataTableValueArray>) => {
          setUsersSelected(value.filter(isSelectedEventUser));
        }}
      >
        {!validateDate && (
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        )}

        {mode === "user" && (
          <Column expander={allowExpansion} style={{ width: "3rem" }} />
        )}

        <Column
          field="userEmail"
          header="Email"
          filter
          filterPlaceholder="Buscar por email"
          showFilterMenu={false}
          filterMatchMode="contains"
          body={(user: EventUsers) => (
            <div className="flex align-items-center gap-2">
              {user.userEmail} <Tooltip target=".custom-target-icon-ban" />
            </div>
          )}
        />
        <Column
          field="userLastName"
          header="Apellido"
          filter
          filterPlaceholder="Buscar por apellido"
          showFilterMenu={false}
          filterMatchMode="contains"
        />
        <Column
          field="userFirstName"
          header="Nombre"
          filter
          filterPlaceholder="Buscar por nombre"
          showFilterMenu={false}
          filterMatchMode="contains"
        />
        {mode === "user" && (
          <Column
            body={PhoneBodyTemplate}
            header="Teléfono"
            field="phone"
            filter
            showFilterMenu={false}
            filterPlaceholder="Buscar por telefono"
            filterMatchMode="contains"
          ></Column>
        )}
        {mode === "user" && (
          <Column
            field="banned"
            header="Ban"
            body={(rowData: EventUsers) => (
              <i className={`pi ${isUserRestrictedByBan(rowData) ? 'pi-ban text-red-500' : 'pi-check text-green-500'}`}
                style={{ fontSize: '1.2rem' }}
                title={isUserRestrictedByBan(rowData) ? 'Usuario restringido' : 'Usuario activo'} />
            )}
          />
        )}
        {mode === "user" && (
          <Column
            field="guests"
            header="Invitados"
            headerStyle={{ width: "9rem" }}
          />
        )}
        {mode === "user" && (
          <Column
            field="Pagos"
            header="Pagos"
            headerStyle={{ width: "9rem" }}
            body={PaymentBodyTemplate}
          />
        )}
      </DataTable>
      {realUsersToAdd && (
        <Dialog
          header="Agregar usuarios reales"
          visible={!!realUsersToAdd}
          style={{ width: "90vw" }}
          onHide={hideAddRealUsersDialog}
        >
          <SelectUsers
            mode="user"
            eventId={realUsersToAdd}
            handleClose={hideAddRealUsersDialog}
          />
        </Dialog>
      )}
      {fictionalUsersToAdd && (
        <Dialog
          header="Agregar usuarios ficticios"
          visible={!!fictionalUsersToAdd}
          style={{ width: "90vw" }}
          onHide={hideAddFictionalUsersDialog}
        >
          <SelectUsers
            mode="fictional"
            eventId={fictionalUsersToAdd}
            handleClose={hideAddFictionalUsersDialog}
          />
        </Dialog>
      )}
      <Dialog
        header="Dar de baja inscripciones"
        visible={showDeleteDialog}
        style={{ width: "32rem", maxWidth: "95vw" }}
        onHide={() => setShowDeleteDialog(false)}
        footer={
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              label="Cancelar"
              text
              onClick={() => setShowDeleteDialog(false)}
            />
            {refundApplies ? (
              <>
                <Button
                  label="Eliminar sin reembolsar"
                  outlined
                  severity="danger"
                  onClick={() => runDelete(selectedUserIds, false)}
                />
                <Button
                  label="Reembolsar y eliminar"
                  severity="danger"
                  onClick={() => runDelete(selectedUserIds, true)}
                />
              </>
            ) : (
              <Button
                label="Eliminar"
                severity="danger"
                onClick={() => runDelete(selectedUserIds, false)}
              />
            )}
          </div>
        }
      >
        <p className="mt-0">
          Vas a dar de baja{" "}
          <strong>
            {usersSelected.length} inscripci{usersSelected.length === 1 ? "ón" : "ones"}
          </strong>
          .
        </p>
        {refundApplies ? (
          <p className="mb-0 text-color-secondary">
            Es un evento pago. <strong>Reembolsar y eliminar</strong> devuelve en MercadoPago
            todo lo que cada inscripto pagó por este evento, incluidos sus invitados, aunque
            falten menos de 24hs para que arranque. Si el reembolso de alguien falla, esa
            inscripción queda activa y te lo avisamos para que reintentes.
          </p>
        ) : (
          <p className="mb-0 text-color-secondary">
            No hay pagos asociados, así que no se devuelve plata.
          </p>
        )}
      </Dialog>
      <Dialog
        header="Inscripciones que no se pudieron dar de baja"
        visible={failedRemovals.length > 0}
        style={{ width: "40rem", maxWidth: "95vw" }}
        onHide={() => setFailedRemovals([])}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Reintentar"
              icon="pi pi-refresh"
              severity="danger"
              onClick={handleRetryFailed}
            />
          </div>
        }
      >
        <p className="mt-0 text-color-secondary">
          Siguen inscriptos y conservan su cupo. El resto del lote ya se dio de baja.
        </p>
        <DataTable value={failedRemovals} size="small" dataKey="userId">
          <Column
            header="Usuario"
            body={(outcome: RemoveEventUserOutcome) => {
              const user = usersById.get(outcome.userId);
              return user
                ? `${user.userFirstName} ${user.userLastName}`
                : `Usuario #${outcome.userId}`;
            }}
          />
          <Column
            header="Email"
            body={(outcome: RemoveEventUserOutcome) =>
              usersById.get(outcome.userId)?.userEmail ?? "-"
            }
          />
          <Column header="Motivo" body={removalFailureReason} />
        </DataTable>
      </Dialog>
    </>
  );
};
